import { ArrowBackRounded, WorkOutlineRounded } from "@mui/icons-material";
import { Box, Card, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { PropsWithChildren } from "react";
import { useSearchParams } from "react-router-dom";
import useUser from "../../providers/user/user_provider";
import { black, greyEnabled, purple, purpleLight } from "../../theme/colors";

const TAB_SUBTITLES = [
  "Manage job listings posted by employers.",
  "Manage job applications submitted by users.",
];

interface JobsLayoutProps {
  currentTab: number;
  onTabChange: (tab: number) => void;
  page?: number;
  setPage?: (page: number) => void;
}

export default function JobsLayout({
  children,
  currentTab,
  onTabChange,
  page,
  setPage,
}: PropsWithChildren<JobsLayoutProps>) {
  const { userPermissions } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleBackClick = () => {
    const currPage = Number(searchParams.get("page")) || 1;
    if (currPage > 1) {
      const newPage = currPage - 1;
      setPage?.(newPage);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("page", (newPage + 1).toString());
          return params;
        },
        { replace: true }
      );
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        {userPermissions?.superAdmin && page !== undefined && page > 1 && (
          <IconButton onClick={handleBackClick}>
            <ArrowBackRounded />
          </IconButton>
        )}
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: purpleLight,
              flexShrink: 0,
            }}
          >
            <WorkOutlineRounded sx={{ color: purple, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color={black}>
              Jobs
            </Typography>
            <Typography variant="body2" color={greyEnabled} mt={0.5}>
              {TAB_SUBTITLES[currentTab]}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Card
        elevation={0}
        sx={{
          display: "flex",
          mt: 2,
          width: "100%",
          minHeight: 600,
          mb: 2,
          borderRadius: 3,
          border: "1px solid #ECECEC",
        }}
      >
        <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: "1px solid #ECECEC", px: 3, pt: 1 }}>
            <Tabs
              value={currentTab}
              aria-label="jobs tabs"
              onChange={(_, value: number) => onTabChange(value)}
              TabIndicatorProps={{
                style: { backgroundColor: purple, height: 3, borderRadius: 3 },
              }}
            >
              <Tab
                label="Job Listing"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: greyEnabled,
                  "&.Mui-selected": { color: purple },
                }}
              />
              <Tab
                label="Job Application"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: greyEnabled,
                  "&.Mui-selected": { color: purple },
                }}
              />
            </Tabs>
          </Box>
          {children}
        </Box>
      </Card>
    </Box>
  );
}
